import { Modal, notification } from 'antd';
import { useForm } from 'antd/es/form/Form';
import ActividadForm from 'components/forms/ActividadForm';
import ActividadMantencionForm from 'components/forms/ActividadMantenciónForm';
import ActivityMaintanceForm from 'components/forms/ActivityMaintanceForm';
import EliminarForm from 'components/forms/EliminarForm';
import ImportActividadesForm from 'components/forms/ImportActividadesForm';
import { useAppApi, usePAppApi } from 'hooks/useAppApi';
import useRequest from 'hooks/useRequest';
import { createContext, useCallback, useContext, useState } from 'react';
import { useColeccion } from './ColeccionProvider';

const AscensorActividadesContext = createContext(null);

const defaultModalProps = {
  children: null,
  title: 'Error',
  open: false,
};

export const AscensorActividadesProvider = ({ children, id }) => {
  const [modalProps, setModalProps] = useState(defaultModalProps);
  const { getAllItems } = useColeccion();

  const map = useCallback((result) => {
    return result;
  }, []);

  const {
    data,
    isLoading: loading,
    error,
    hasCalled,
    call: notFunctionalCall,
  } = useAppApi({
    baseUrl: '/actividad/ascensores',
    mapResults: map,
  });

  const call = useCallback(
    async (id_ascensor, template = false) => {
      const result = await notFunctionalCall({
        extUrl: `/${id_ascensor}`,
        queryParams: {
          template,
        },
      });

      return result;
    },
    [notFunctionalCall]
  );

  const { request, data: reqData, errors, isLoading } = useRequest();
  const [isLoadingAdd, setIsLoadingAdd] = useState(false);
  const ADD_URL = '/actividad';

  const [form] = useForm();
  const add = useCallback(
    async (values) => {
      try {
        await form.validateFields();
        if (values.tipo_intervalo?.length === 1) {
          let postValues = {
            ...values,
            tipo_intervalo: values.tipo_intervalo[0],
          };
          const response = await request(ADD_URL, 'POST', postValues);
          if (values?.id_ascensor) {
            call(values?.id_ascensor);
          }
          setModalProps(defaultModalProps);
          setIsLoadingAdd(false);
          return response;
        } else {
          notification.error({
            message: 'Error al agregar la actividad',
          });
          setIsLoadingAdd(false);
        }
      } catch (error) {
        setIsLoadingAdd(false);
        notification.error({
          message: 'Error al agregar la actividad',
          description: error.message,
        });
      }
    },
    [request, form, call]
  );

  const edit = useCallback(
    async (values, id) => {
      try {
        await form.validateFields();
        if (values.tipo_intervalo?.length === 1) {
          let postValues = {
            ...values,
            tipo_intervalo: values.tipo_intervalo[0],
          };
          const response = await request(`${ADD_URL}/${id}`, 'PUT', postValues);
          if (values?.id_ascensor) {
            call(values?.id_ascensor);
          }
          setModalProps(defaultModalProps);
          return response;
        } else {
          notification.error({
            message: 'Error al agregar la actividad',
          });
        }
      } catch (error) {
        notification.error({
          message: 'Error al editar la actividad',
          description: error.message,
        });
      }
    },
    [request, form, call]
  );

  const deleteActividad = async (id, id_ascensor, refresh) => {
    try {
      const response = await request(`${ADD_URL}/${id}`, 'DELETE');
      if (refresh) {
        refresh();
      }
      return response;
    } catch (error) {
      notification.error({
        message: 'Error al eliminar la actividad',
        description: error.message,
      });
    }
  };

  const openAdd = async (id_ascensor, refresh) => {
    setModalProps({
      open: true,
      children: (
        <ActividadForm
          id={id_ascensor}
          id_field="id_ascensor"
          onFinish={(data, idItem) => {
            add({ ...data, id_ascensor: id_ascensor });
            if (refresh) {
              refresh();
            }
          }}
        />
      ),
      title: 'Agregar Actividad',
    });
  };

  const openEdit = async (record, refresh) => {
    setModalProps({
      open: true,
      children: (
        <ActividadForm
          id={record.id_cliente || record.id_ascensor}
          id_field={record.id_cliente ? 'id_cliente' : 'id_ascensor'}
          initialValues={record}
          onFinish={(data, idItem) => {
            edit({ ...data, id_ascensor: record.id_ascensor }, record.id);
            if (refresh) {
              refresh();
            }
          }}
        />
      ),
      title: 'Editar Actividad',
    });
  };

  const openDelete = async (id, id_ascensor, refresh) => {
    setModalProps({
      open: true,
      children: (
        <EliminarForm
          title="Eliminar Actividad"
          id={id}
          deleteRegister={async (id) => {
            const response = await deleteActividad(id, id_ascensor, refresh);
            if (refresh) {
              refresh();
            }
            return response;
          }}
        />
      ),
      title: 'Eliminar Actividad',
    });
  };

  const openImport = async (id_ascensor, refresh) => {
    setModalProps({
      open: true,
      children: (
        <ImportActividadesForm
          id={id_ascensor}
          id_field="id_ascensor"
          onFinish={(data, idItem) => {
            refresh(idItem);
            setModalProps(defaultModalProps);
          }}
        />
      ),
      title: 'Importar Actividades',
      width: 700,
    });
  };

  return (
    <>
      <Modal
        footer={false}
        onCancel={() => setModalProps(defaultModalProps)}
        {...modalProps}
      />
      <AscensorActividadesContext.Provider
        value={{
          call,
          id,
          loading,
          error,
          hasCalled,
          data,
          openAdd,
          isLoadingAdd: isLoading(ADD_URL),
          openEdit,
          openDelete,
          openImport,
        }}
      >
        {children}
      </AscensorActividadesContext.Provider>
    </>
  );
};

export const useAscensorActividades = () =>
  useContext(AscensorActividadesContext);

